/**
 * Maintenance Mode — who the maintenance screen may be shown to.
 *
 * Kept as a dependency-free helper (and not inline in the store) so the rule can be unit tested
 * and so there is exactly one place the SPA decides it. The server decides the same thing at
 * sign-in and in `myTenantSetting`; this must never contradict it.
 */
export interface MaintenanceAccessInput {
  /** Maintenance is on for this user's tenant (global OR tenant switch, already resolved). */
  enabled: boolean
  /** The master admin ticked "allow tenant admin" for this tenant. */
  allowTenantAdmin: boolean
  /** The signed-in user holds the tenant's ADMIN role (alongside any other roles). */
  isTenantAdmin: boolean
}

/**
 * True when the application must be replaced by the maintenance notice.
 *
 * A tenant ADMIN is exempt whenever "allow tenant admin" is on — that option exists so an admin
 * can sign in and verify the tenant during maintenance, which is impossible if the SPA blanks the
 * application straight after they are let through the sign-in guard. Holding other roles as well
 * (ADMIN_STAFF, STAFF, …) never withdraws the exemption: ADMIN is the tenant's highest authority.
 */
export const isMaintenanceBlocking = ({
  enabled,
  allowTenantAdmin,
  isTenantAdmin,
}: MaintenanceAccessInput): boolean => {
  if (!enabled) {
    return false
  }
  return !(allowTenantAdmin && isTenantAdmin)
}
